import React from 'react';
import {
    Row, Col, FormGroup, Button,
} from 'reactstrap';
import { mapValues } from 'lodash-es';
import * as moment from 'moment';
import cls from 'classnames';
import { faMinusCircle } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, {
    MomentDateFormat, ApiRoutes, ChargeTypeResourceTypes, ChargeTypeUnits,
} from '../Common';
import SlideForm from '../common/forms/SlideForm';
import {
    FormLabel, FormCheckbox, FlexEndRow,
    Input,
    NumericInput,
} from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { NotAuthorizedPage } from '../status/StatusCodes';
import { BillingRateCountyGroup } from './BillingRateCountyGroup';
import { BillingRateTypes } from './BillingRate';
import './BillingRateCountyGroup.scss';

export default class BillingRateForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            loading: true,
            errors: {},
            formValidated: false,
            validationMessage: '',
            isValidated: false,
            inventoryItems: [],
            rentalUnitTypes: [],
            associateInventoryItem: false,
        };
    }

    componentDidMount() {
        this.populateState();
    }

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    onRemove = () => {
        this.context.setFormOpened(false);
        this.props.onRemoveCallback();
    };

    onSubmit = () => {
        this.context.setFormOpened(false);
        this.props.onSaveCallback();
    };

    async populateState() {
        const [inventoryItems, rentalUnitTypes] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.quickBooksInventoryItems()),
            httpClient.js(ApiRoutes.typeAheads.rentalUnitTypes()),
        ]);

        this.setState({
            loading: false,
            inventoryItems,
            rentalUnitTypes,
            associateInventoryItem: this.context.tenant.tenantSettings.useQuickBooksInvoicing,
        });
    }

    resetForm = () => this.setState({ formValidated: false, errors: {} });

    getEntityName = () => this.props.entityName ? `${this.props.entityName}.` : '';

    excludeEmptyRates = () => {
        const { entity, onChange } = this.props;

        const countyGroups = Object.values(entity.usCountyGroupBillingRates || {});
        const jobTypeRates = Object.values(entity.jobTypeBillingRates || {});
        const customRate = mapValues((countyGroups.length > 0 ? countyGroups : jobTypeRates) || {}, (rate) => {
            if (!rate.billingRate.rate) {
                return {
                    ...rate,
                    billingRate: {
                        ...rate.billingRate,
                        current: false,
                    },
                };
            }

            return rate;
        });

        onChange({
            target: {
                name: `${this.getEntityName()}${countyGroups.length > 0 ? 'usCountyGroupBillingRates' : 'jobTypeBillingRates'}`,
                value: customRate,
            },
        });
    };

    render() {
        const { tenantSettings } = (this.context ?? {}).tenant ?? {};
        if (!tenantSettings.billingRatesEnabled) return <NotAuthorizedPage />;

        if (!this.props.entity) return '';
        const { chargeTypeReadonly, entity } = this.props;
        const { rateType } = entity;

        const name = this.getEntityName();
        const isChargeType = rateType === BillingRateTypes.CHARGE_TYPE;

        const chargeTypes = isChargeType ? this.props.chargeTypes ?? [] : this.props.chargeTypeGroups ?? [];
        const inventoryItems = this.state.inventoryItems ?? [];
        const rentalUnitTypes = this.state.rentalUnitTypes ?? [];

        const countyGroups = Object.values(entity.usCountyGroupBillingRates || {});
        const hasCountyGroups = countyGroups.length > 0;

        const jobTypesBillingRates = Object.values(entity.jobTypeBillingRates || {});
        const customRates = hasCountyGroups ? countyGroups : jobTypesBillingRates;
        const customRatesType = hasCountyGroups ? 'usCountyGroupBillingRates' : 'jobTypeBillingRates';


        const selectedChargeType = chargeTypes?.find((i) => (i.value ?? i.id) === (entity.chargeTypeId ?? entity.chargeTypeGroupId));

        const isRentalChargeType = selectedChargeType?.resourceTypeId === ChargeTypeResourceTypes.Rental;
        const isPercentageSurcharge = selectedChargeType?.unitsId === ChargeTypeUnits.Percentage;
        const isFlatCharge = selectedChargeType?.unitsId === ChargeTypeUnits.Flat;
        const descriptionWidth = isRentalChargeType ? 3 : 6;

        const overTimeEnabled =
            (hasCountyGroups && isChargeType)
            || (!hasCountyGroups && !isRentalChargeType && !isFlatCharge && !isPercentageSurcharge);

        const doubleTimeEnabled =
            !!this.props.showDoubleTime
            && selectedChargeType?.resourceTypeId === ChargeTypeResourceTypes.Employee
            && selectedChargeType?.unitsId === ChargeTypeUnits.Hours
            && !!tenantSettings.enableDoubleTimeBilling;

        const shiftDifferentialEnabled =
            !!this.props.showShiftDifferential
            && selectedChargeType?.resourceTypeId === ChargeTypeResourceTypes.Employee
            && selectedChargeType?.unitsId === ChargeTypeUnits.Hours
            && !!tenantSettings.enableShiftDifferentialBilling;

        const field = isChargeType ? `${name}chargeTypeId` : `${name}chargeTypeGroupId`;

        return (
            <SlideForm
                size="col-xl-6 col-md-6 col-xs-12"
                loading={this.state.loading}
                show={this.props.show}
                id="billingRateForm"
                formIcon={faMinusCircle}
                formTitle={
                    !!parseInt(this.props.entity.id ?? 0) > 0
                        ? 'Edit Rate'
                        : 'Add Rate'
                }
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={this.state.formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                errors={this.state.errors}
                onClearErrors={this.onClearErrors}
                error={this.state.validationMessage}
                showDelete={this.props.showRemove === true}
                onDelete={this.onRemove}
            >
                <>
                    {!!this.props.showChargeType == true && (
                        <>
                            {!chargeTypeReadonly ? (
                                <ValidatedSelect
                                    formGroupClassName="ml-3 w-auto"
                                    label={isChargeType ? 'Charge Type' : 'Charge Type Group'}
                                    required={!selectedChargeType}
                                    isClearable={false}
                                    id={field}
                                    name={field}
                                    value={selectedChargeType}
                                    options={chargeTypes ?? []}
                                    onChange={this.props.onSelectChange}
                                    readOnly
                                    getOptionLabel={(o) => o.label}
                                    getOptionValue={(o) => o.value}
                                    error="A charge is required."
                                />
                            ) : (
                                <Input
                                    formGroupClassName="ml-3 w-auto"
                                    readOnly
                                    value={entity.chargeTypeName ?? entity.chargeTypeGroupName}
                                    label={isChargeType ? 'Charge Type' : 'Charge Type Group'}
                                    required={!selectedChargeType}
                                    error="A charge is required."
                                />
                            )}

                            {Boolean(selectedChargeType) && !hasCountyGroups && (
                                <>
                                    <Input
                                        label="Minimum Bill Hours"
                                        formGroupClassName="ml-3 w-auto"
                                        readOnly
                                        value={
                                            selectedChargeType?.minimumBillUnits
                                                ? selectedChargeType.minimumBillUnits.toFixed(
                                                    2,
                                                )
                                                : 'N/A'
                                        }
                                        placeholder="Min Bill Hours"
                                    />
                                    <Input
                                        label="Maximum Bill Hours"
                                        formGroupClassName="ml-3 w-auto"
                                        readOnly
                                        value={
                                            selectedChargeType.maximumBillUnits
                                                ? selectedChargeType.maximumBillUnits.toFixed(
                                                    2,
                                                )
                                                : 'N/A'
                                        }
                                        placeholder="Max Bill Hours"
                                    />

                                </>
                            )}
                        </>
                    )}

                    {Boolean(hasCountyGroups) && (
                        <Row className="mb-1">
                            <Col
                                xs="12"
                                className="ml-3"
                            >
                                <strong className="text-muted">Regular Rate</strong>
                            </Col>
                        </Row>
                    )}

                    <Row className={cls(hasCountyGroups && 'mb-3 pt-2 bg-light ml-3 mr-3')}>
                        {!hasCountyGroups && (
                            <Col xs="3">
                                <NumericInput
                                    label="Rate"
                                    formGroupClassName="ml-3 w-auto"
                                    id={`${name}billingRate.rate`}
                                    name={`${name}billingRate.rate`}
                                    value={entity.billingRate.rate ?? {}}
                                    onChange={this.props.onChange}
                                    disabled={isPercentageSurcharge}
                                    placeholder="Enter Rate"
                                    step="0.001"
                                    required={!isPercentageSurcharge}
                                    error="Rate is required."
                                    fractionDigits={3}
                                />
                            </Col>
                        )}

                        <Col xs={this.state.associateInventoryItem ? descriptionWidth : '9'}>
                            <Input
                                label="Description"
                                formGroupClassName={hasCountyGroups ? '' : 'w-auto'}
                                id={`${name}billingRate.billingRaterateDescription`}
                                name={`${name}billingRate.rateDescription`}
                                value={
                                    entity.billingRate.rateDescription ?? ''
                                }
                                onChange={this.props.onChange}
                                disabled={isPercentageSurcharge}
                                placeholder="Rate Description"
                                maxLength="100"
                            />
                        </Col>

                        {Boolean(isRentalChargeType) && (
                            <Col xs={3}>
                                <ValidatedSelect
                                    className="w-auto"
                                    id="rentalUnitTypeId"
                                    label="Rental Unit"
                                    name={`${name}billingRate.rentalUnitTypeId`}
                                    options={rentalUnitTypes}
                                    required
                                    error="A rental unit type is required"
                                    value={
                                        (rentalUnitTypes ?? []).find(
                                            (s) => s.value
                                                == entity.billingRate
                                                    .rentalUnitTypeId,
                                        ) ?? ''
                                    }
                                    onChange={this.props.onSelectChange}
                                    disabled={isPercentageSurcharge}
                                />
                            </Col>
                        )}

                        {/* If the tenant setting exists and there are inventory items to load */}
                        {this.state.associateInventoryItem
                            && inventoryItems.length > 0 && (
                                <Col xs="3">
                                    <ValidatedSelect
                                        className="w-auto"
                                        id="rateQBInventoryItemId"
                                        label="Inventory Item"
                                        name={`${name}billingRate.rateQBInventoryItemId`}
                                        options={inventoryItems}
                                        value={
                                            (inventoryItems ?? []).find(
                                                (s) => s.value
                                                    == entity.billingRate
                                                        .rateQBInventoryItemId,
                                            ) ?? ''
                                        }
                                        onChange={this.props.onSelectChange}
                                    />
                                </Col>
                            )}
                    </Row>

                    {Boolean(isChargeType) && !isRentalChargeType && !isFlatCharge && !isPercentageSurcharge && (
                        <>
                            {Boolean(this.props.showShiftDifferential) && (
                                <>
                                    {Boolean(hasCountyGroups) && (
                                        <Row className="mb-1">
                                            <Col
                                                xs="12"
                                                className="ml-3"
                                            >
                                                <strong className="text-muted">Shift Differential Rate</strong>
                                            </Col>
                                        </Row>
                                    )}

                                    <Row className={cls(hasCountyGroups && 'mb-3 pt-2 bg-light ml-3 mr-3')}>
                                        {!hasCountyGroups && (
                                            <Col xs="3">
                                                <NumericInput
                                                    label="Shift Differential Rate"
                                                    formGroupClassName="w-auto ml-3"
                                                    id={`${name}billingRate.sdate`}
                                                    name={`${name}billingRate.sdRate`}
                                                    value={entity.billingRate.sdRate ?? {}}
                                                    onChange={this.props.onChange}
                                                    placeholder="Enter RSD Rate"
                                                    step="0.001"
                                                    disabled={!shiftDifferentialEnabled}
                                                    fractionDigits={3}
                                                />
                                            </Col>
                                        )}

                                        <Col xs={this.state.associateInventoryItem ? '6' : '9'}>
                                            <Input
                                                label="Description"
                                                formGroupClassName={hasCountyGroups ? '' : 'w-auto'}
                                                id={`${name}billingRate.sdRateDescription`}
                                                name={`${name}billingRate.sdRateDescription`}
                                                value={entity.billingRate.sdRateDescription ?? ''}
                                                onChange={this.props.onChange}
                                                placeholder="Rate Description"
                                                maxLength="100"
                                                disabled={!shiftDifferentialEnabled}
                                            />
                                        </Col>
                                        {/* If the tenant setting exists and there are inventory items to load */}
                                        {this.state.associateInventoryItem && inventoryItems.length > 0 && (
                                            <Col xs="3">
                                                <ValidatedSelect
                                                    className="w-auto"
                                                    label="Inventory Item"
                                                    id="sdRateQBInventoryItemId"
                                                    name={`${name}billingRate.sdRateQBInventoryItemId`}
                                                    disabled={!shiftDifferentialEnabled}
                                                    options={inventoryItems}
                                                    value={
                                                        (inventoryItems ?? []).find(
                                                            (s) => s.value
                                                                == entity.billingRate
                                                                    .sdRateQBInventoryItemId,
                                                        ) ?? ''
                                                    }
                                                    onChange={this.props.onSelectChange}
                                                />
                                            </Col>
                                        )}
                                    </Row>
                                </>
                            )}

                            {Boolean(hasCountyGroups) && (
                                <Row className="mb-1">
                                    <Col
                                        xs="12"
                                        className="ml-3"
                                    >
                                        <strong className="text-muted">Overtime Rate</strong>
                                    </Col>
                                </Row>
                            )}

                            <Row className={cls(hasCountyGroups && 'mb-3 pt-2 bg-light ml-3 mr-3')}>
                                {!hasCountyGroups && (
                                    <Col xs="3">
                                        <NumericInput
                                            label="Overtime Rate"
                                            formGroupClassName="w-auto ml-3"
                                            id={`${name}billingRate.otRate`}
                                            name={`${name}billingRate.otRate`}
                                            value={entity.billingRate.otRate ?? {}}
                                            onChange={this.props.onChange}
                                            disabled={isPercentageSurcharge}
                                            placeholder="Enter Overtime Rate"
                                            step="0.001"
                                            fractionDigits={3}
                                        />
                                    </Col>
                                )}

                                <Col xs={this.state.associateInventoryItem ? '6' : '9'}>
                                    <Input
                                        label="Description"
                                        formGroupClassName={hasCountyGroups ? '' : 'w-auto'}
                                        id={`${name}billingRate.otRateDescription`}
                                        name={`${name}billingRate.otRateDescription`}
                                        value={
                                            entity.billingRate.otRateDescription ?? ''
                                        }
                                        onChange={this.props.onChange}
                                        placeholder="Rate Description"
                                        maxLength="100"
                                        disabled={isPercentageSurcharge}
                                    />
                                </Col>
                                {/* If the tenant setting exists and there are inventory items to load */}
                                {this.state.associateInventoryItem && inventoryItems.length > 0 && (
                                    <Col xs="3">
                                        <ValidatedSelect
                                            className="w-auto"
                                            id="otRateQBInventoryItemId"
                                            label="Inventory Item"
                                            name={`${name}billingRate.otRateQBInventoryItemId`}
                                            options={inventoryItems}
                                            value={
                                                (inventoryItems ?? []).find(
                                                    (s) => s.value
                                                        == entity.billingRate
                                                            .otRateQBInventoryItemId,
                                                ) ?? ''
                                            }
                                            onChange={this.props.onSelectChange}
                                        />
                                    </Col>
                                )}
                            </Row>

                            {Boolean(this.props.showDoubleTime) && (
                                <>
                                    {Boolean(hasCountyGroups) && (
                                        <Row className="mb-1">
                                            <Col
                                                xs="12"
                                                className="ml-3"
                                            >
                                                <strong className="text-muted">Double Rate</strong>
                                            </Col>
                                        </Row>
                                    )}

                                    <Row className={cls(hasCountyGroups && 'mb-3 pt-2 bg-light ml-3 mr-3')}>
                                        {!hasCountyGroups && (
                                            <Col xs="3">
                                                <NumericInput
                                                    label="Double-Time Rate"
                                                    formGroupClassName="ml-3 w-auto"
                                                    id={`${name}billingRate.altRate`}
                                                    name={`${name}billingRate.altRate`}
                                                    value={entity.billingRate.altRate ?? {}}
                                                    onChange={this.props.onChange}
                                                    placeholder="Enter DT Rate"
                                                    step="0.001"
                                                    disabled={!doubleTimeEnabled || isPercentageSurcharge}
                                                    fractionDigits={3}
                                                />
                                            </Col>
                                        )}

                                        <Col xs={this.state.associateInventoryItem ? '6' : '9'}>
                                            <Input
                                                label="Description"
                                                formGroupClassName={hasCountyGroups ? '' : 'w-auto'}
                                                id={`${name}billingRate.altRateDescription`}
                                                name={`${name}billingRate.altRateDescription`}
                                                value={entity.billingRate.altRateDescription ?? ''}
                                                onChange={this.props.onChange}
                                                placeholder="Rate Description"
                                                maxLength="100"
                                                disabled={!doubleTimeEnabled || isPercentageSurcharge}
                                            />
                                        </Col>
                                        {/* If the tenant setting exists and there are inventory items to load */}
                                        {this.state.associateInventoryItem && inventoryItems.length > 0 && (
                                            <Col xs="3">
                                                <ValidatedSelect
                                                    className="w-auto"
                                                    id="altRateQBInventoryItemId"
                                                    label="Inventory Item"
                                                    name={`${name}billingRate.altRateQBInventoryItemId`}
                                                    disabled={!doubleTimeEnabled}
                                                    options={inventoryItems}
                                                    value={
                                                        (inventoryItems ?? []).find(
                                                            (s) => s.value
                                                                == entity.billingRate
                                                                    .altRateQBInventoryItemId,
                                                        ) ?? ''
                                                    }
                                                    onChange={this.props.onSelectChange}
                                                />
                                            </Col>
                                        )}
                                    </Row>
                                </>
                            )}
                        </>
                    )}


                    <FormGroup>
                        <FormCheckbox
                            className="ml-3 w-auto"
                            id={`${name}billingRate.isTaxable`}
                            name={`${name}billingRate.isTaxable`}
                            checked={entity.billingRate.isTaxable ?? ''}
                            onChange={this.props.onChange}
                            labelText="Is Taxable"
                        />
                    </FormGroup>

                    <Input
                        label="Effective Date"
                        formGroupClassName="ml-3 w-auto"
                        id={`${name}billingRate.effectiveDate`}
                        name={`${name}billingRate.effectiveDate`}
                        onChange={this.props.onChange}
                        value={
                            moment(entity.billingRate.effectiveDate).format(
                                MomentDateFormat.DateOnlyUTCHyphenated,
                            ) ?? ''
                        }
                        type="date"
                        required
                        error="Effective Date is required."
                    />
                    <Input
                        label="Expiration Date"
                        formGroupClassName="ml-3 w-auto"
                        id={`${name}billingRate.expirationDate`}
                        name={`${name}billingRate.expirationDate`}
                        value={
                            moment(
                                entity.billingRate.expirationDate,
                            ).format(
                                MomentDateFormat.DateOnlyUTCHyphenated,
                            ) ?? ''
                        }
                        onChange={this.props.onChange}
                        type="date"
                    />

                    {!countyGroups?.length && (
                        <FormGroup>
                            <FormLabel
                                className="ml-3"
                                label="Active"
                                required={false}
                            />
                            <FormCheckbox
                                className="ml-3 w-auto"
                                id={`${name}billingRate.current`}
                                name={`${name}billingRate.current`}
                                checked={entity.billingRate.current ?? ''}
                                onChange={this.props.onChange}
                                labelText="Active"
                            />
                        </FormGroup>
                    )}

                    {customRates.length > 0 && (
                        <>
                            <table className="table table-sm countyGroupTimesheetTable borderless">
                                <thead>
                                    <tr>
                                        {hasCountyGroups ? (
                                            <>
                                                <th className="badge-column" />
                                                <th>Group</th>
                                            </>
                                        ) : (
                                            <th>Job type</th>
                                        )}
                                        <th className="not-applicable">
                                            Exclude
                                        </th>
                                        <th className="numeric-input">
                                            REG
                                        </th>
                                        {Boolean(isChargeType) && (
                                            <>
                                                {Boolean(this.props.showShiftDifferential) && (
                                                    <th className="numeric-input">
                                                        RSD
                                                    </th>
                                                )}

                                                <th className="numeric-input">
                                                    OT
                                                </th>

                                                {Boolean(this.props.showDoubleTime) && (
                                                    <th className="numeric-input">
                                                        DOUBLE
                                                    </th>
                                                )}
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {customRates.map((rate, index) => (
                                        <BillingRateCountyGroup
                                            key={rate.usCountyGroup?.id ?? rate.jobTypeId}
                                            entityName={name}
                                            entityType={customRatesType}
                                            onChange={this.props.onChange}
                                            index={index}
                                            doubleTimeEnabled={doubleTimeEnabled}
                                            overTimeEnabled={overTimeEnabled}
                                            showOverTime={Boolean(isChargeType)}
                                            showDoubleTime={Boolean(isChargeType) && this.props.showDoubleTime}
                                            showShiftDifferential={Boolean(isChargeType) && this.props.showShiftDifferential}
                                            shiftDifferentialEnabled={shiftDifferentialEnabled}
                                            isUsCountyGroup={hasCountyGroups}
                                            jobTypeName={rate.jobTypeName}
                                            {...rate.usCountyGroup}
                                            {...rate.billingRate}
                                        />
                                    ))}
                                </tbody>
                            </table>
                            <Row>
                                <Col>
                                    <FlexEndRow className="pt-1 pl-3 pr-4">
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="site-link w-100 text-right"
                                            role="button"
                                            onClick={this.excludeEmptyRates}
                                        >
                                            Exclude All Empty Rates
                                        </Button>
                                    </FlexEndRow>
                                </Col>
                            </Row>
                        </>
                    )}
                </>
            </SlideForm>
        );
    }
}
